/**
 * API client.
 *
 * A thin typed wrapper over `fetch` rather than axios: the app needs auth
 * headers, JSON handling and a consistent error type, and that is about 60
 * lines. Query caching is TanStack Query's job, not the transport's.
 */

import type {
  AggregatedSamples,
  CrossingEvents,
  CrossingLatest,
  CrossingPeaks,
  CrossingSummary,
  CrossingTrace,
  DeriveStrainResult,
  FatigueSummary,
  Alarm,
  ApiKey,
  DeviceBootstrap,
  DeviceKey,
  DeviceType,
  DeviceTypeTemplate,
  Grant,
  NewChannel,
  PermissionGroup,
  User,
  AlarmComment,
  AlarmLog,
  AlarmSummary,
  ChannelLimit,
  ChannelLimitIn,
  Notifier,
  NotifierTest,
  Channel,
  CurrentUser,
  Device,
  DisplayConfig,
  EventCycleHistogram,
  EventCycleTimeline,
  KEvent,
  EventStatistics,
  LatestSample,
  MetricExtreme,
  MetricSample,
  Project,
  RemnantCapacity,
  SecondarySample,
  Waveform,
} from "./types";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string) ?? "/api";

const TOKEN_KEY = "kinetix.token";
const REFRESH_KEY = "kinetix.refresh";
const APIKEY_KEY = "kinetix.apikey";

/** Fired on window when the session cannot be renewed; App sends the user to /login. */
export const SESSION_EXPIRED_EVENT = "kinetix:session-expired";

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public context?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }

  get isAuthError() {
    return this.status === 401 || this.status === 403;
  }
}

export const auth = {
  get token() {
    return localStorage.getItem(TOKEN_KEY);
  },
  get apiKey() {
    return localStorage.getItem(APIKEY_KEY);
  },
  get refreshToken() {
    return localStorage.getItem(REFRESH_KEY);
  },
  setToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.removeItem(APIKEY_KEY);
  },
  /** Access token plus the single-use refresh token that renews it. */
  setSession(token: string, refresh: string | null | undefined) {
    localStorage.setItem(TOKEN_KEY, token);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    else localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(APIKEY_KEY);
  },
  setApiKey(key: string) {
    localStorage.setItem(APIKEY_KEY, key);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(APIKEY_KEY);
  },
  get isAuthenticated() {
    return Boolean(this.token || this.apiKey);
  },
};

/** Build a query string, dropping empties and expanding arrays to repeats. */
function qs(params?: Record<string, unknown>): string {
  if (!params) return "";
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      // FastAPI reads repeated keys as a list; a comma-joined string would
      // arrive as one long value.
      value.forEach((v) => search.append(key, String(v)));
    } else {
      search.append(key, String(value));
    }
  }
  const text = search.toString();
  return text ? `?${text}` : "";
}

/** An uploaded 3D model, as listed. */
/** The Rockfield Mercuto forwarder's settings, as the API reports them. */
export interface MercutoSettings {
  project: string;
  enabled: boolean;
  broker_host: string;
  broker_port: number;
  use_tls: boolean;
  username: string;
  /** The password is write-only; whether one exists is all the page can ask. */
  password_set: boolean;
  client_id: string;
  subscriber_username: string;
  subscriber_issued: boolean;
  topic_template: string;
  topic_prefix: string;
  example_topic: string;
  subscribe_filter: string;
  qos: number;
  retain: boolean;
  decimals: number;
  send_crossings: boolean;
  send_periodic: boolean;
  send_health: boolean;
  send_rainflow: boolean;
  send_trace: boolean;
  devices: string[];
  last_event_id: number;
  sent_count: number;
  failed_count: number;
  last_sent_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
}

/** Shown once, at the moment it is issued. */
export interface MercutoSubscriber {
  username: string;
  password: string;
  host: string;
  port: number;
  use_tls: boolean;
  subscribe_filter: string;
  issued_at: string;
}

export interface TwinModelRow {
  code: string;
  project: string;
  name: string;
  filename: string;
  size_bytes: number;
  created_at: string;
  /** "model" (.glb) or "drawing" (PNG/JPEG/WebP elevation). */
  kind?: "model" | "drawing";
  url: string;
  warning?: string | null;
}

/**
 * The raw bytes of an uploaded model.
 *
 * Not through `request`, which parses JSON: this is a binary body. It still
 * sends the same credentials, which is the reason models are fetched here at
 * all rather than handed to the 3D loader as a URL - the loader's own fetch
 * would go out without them and be refused.
 */
/* ─── Staying signed in ───────────────────────────────────────────────────
 *
 * The access token lasts hours; the refresh token 30 days, renewed on every
 * use. A request refused with 401 trades the refresh token for a new pair and
 * is sent again, so an expiry is never seen. Only when that fails - idle for a
 * month, signed out elsewhere, account disabled - does the app go to /login.
 */
let refreshing: Promise<boolean> | null = null;
let expiredSignalled = false;

/** One refresh per tab at a time: twenty queries failing together share it. */
function refreshSession(): Promise<boolean> {
  if (!refreshing) {
    refreshing = doRefresh().finally(() => {
      refreshing = null;
    });
  }
  return refreshing;
}

async function doRefresh(): Promise<boolean> {
  const spent = auth.refreshToken;
  if (!spent) return false;
  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: spent }),
    });
  } catch {
    // Offline or the server is down: not a reason to sign anyone out. The
    // request fails as it would have; the next one tries again.
    throw new ApiError(0, "network", "could not reach the server");
  }
  if (response.ok) {
    const data = (await response.json()) as { access_token: string; refresh_token?: string };
    auth.setSession(data.access_token, data.refresh_token);
    return true;
  }
  // Another tab refreshed with the same token a moment earlier and stored
  // the new pair; this tab picks it up rather than signing out.
  const body = safeJson(await response.text()) as { detail?: string } | null;
  if (response.status === 401 && body?.detail?.includes("already used")) {
    for (let i = 0; i < 10; i++) {
      if (auth.refreshToken && auth.refreshToken !== spent) return true;
      await new Promise((r) => setTimeout(r, 100));
    }
  }
  return false;
}

/** The session is over: clear it and let the app take the user to /login. */
function sessionExpired() {
  auth.clear();
  if (expiredSignalled) return;
  expiredSignalled = true;
  window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
  setTimeout(() => {
    expiredSignalled = false;
  }, 2000);
}

/**
 * fetch with the stored credentials, renewing an expired session once.
 * 403 is left alone - that is "not allowed", not "not signed in".
 */
async function authedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers);
    if (auth.token) headers.set("Authorization", `Bearer ${auth.token}`);
    else if (auth.apiKey) headers.set("X-API-Key", auth.apiKey);
    return fetch(url, { ...init, headers });
  };
  const response = await send();
  if (response.status !== 401 || (!auth.token && !auth.apiKey)) return response;
  if (auth.token && (await refreshSession())) {
    const retried = await send();
    if (retried.status !== 401) return retried;
  }
  sessionExpired();
  return response;
}

async function fetchBytes(path: string): Promise<ArrayBuffer> {
  const response = await authedFetch(`${BASE_URL}${path}`);
  if (!response.ok) {
    const text = await response.text();
    const body = text ? safeJson(text) : null;
    throw new ApiError(
      response.status,
      (body as { error?: string })?.error ?? "http_error",
      formatDetail(body, response.statusText || "request failed"),
      (body as { context?: unknown })?.context,
    );
  }
  return response.arrayBuffer();
}

async function request<T>(
  path: string,
  init: RequestInit & { params?: Record<string, unknown> } = {},
): Promise<T> {
  const { params, ...rest } = init;
  const headers = new Headers(rest.headers);

  if (rest.body && !(rest.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await authedFetch(`${BASE_URL}${path}${qs(params)}`, { ...rest, headers });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? safeJson(text) : null;

  if (!response.ok) {
    const code = (body as { error?: string })?.error ?? "http_error";
    throw new ApiError(
      response.status,
      code,
      formatDetail(body, response.statusText || "request failed"),
      (body as { context?: unknown })?.context,
    );
  }

  return body as T;
}

/**
 * Turn an error body into something a human can act on.
 *
 * FastAPI reports validation failures as `{"detail": [{loc, msg, type}, ...]}` —
 * an array, not a string. Handing that to `new Error()` renders it as
 * "[object Object]", which tells the reader nothing at all. Flattening
 * `loc` gives "channels.0.project: Field required", which names the field.
 */
function formatDetail(body: unknown, fallback: string): string {
  const detail = (body as { detail?: unknown })?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return (
      detail
        .map((entry) => {
          const e = entry as { loc?: unknown[]; msg?: string; type?: string };
          // Drop the leading "body"/"query" frame; it is noise to the reader.
          const path = Array.isArray(e.loc)
            ? e.loc.filter((p) => p !== "body" && p !== "query").join(".")
            : "";
          const message = e.msg ?? e.type ?? "invalid";
          return path ? `${path}: ${message}` : message;
        })
        .join("; ") || fallback
    );
  }

  if (detail && typeof detail === "object") return JSON.stringify(detail);
  return fallback;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

export const api = {
  // -- auth ---------------------------------------------------------------
  async login(email: string, password: string) {
    // OAuth2 password flow takes form encoding, not JSON.
    const form = new URLSearchParams({ username: email, password });
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form,
    });
    if (!response.ok) {
      const body = safeJson(await response.text()) as { detail?: string };
      throw new ApiError(response.status, "auth_failed", body?.detail ?? "login failed");
    }
    const data = (await response.json()) as { access_token: string; refresh_token?: string };
    auth.setSession(data.access_token, data.refresh_token);
    return data;
  },

  /** End this browser's session on the server too. Best effort: signing out
   * locally must work even when the server cannot be reached. */
  async logout() {
    const refresh = auth.refreshToken;
    auth.clear();
    if (!refresh) return;
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
    } catch {
      /* already signed out locally */
    }
  },

  me: () => request<CurrentUser>("/identity/users/me"),

  // -- assets -------------------------------------------------------------
  projects: () => request<Project[]>("/assets/projects", { params: { include_status: true } }),
  project: (code: string) => request<Project>(`/assets/projects/${code}`),
  display: (code: string) => request<DisplayConfig>(`/assets/projects/${code}/display`),

  /**
   * Replace one section of a project's display config.
   *
   * Section-at-a-time, matching the endpoint: sending the whole config back
   * would make two people editing different parts clobber each other.
   */
  /**
   * Removes one dashboard and leaves the rest exactly as stored. Not a PUT of
   * the remaining list: that re-validates every other dashboard, and one old
   * layout the schema now refuses would block every delete on the project.
   */
  deleteDashboard: (code: string, dashboardId: string) =>
    request<DisplayConfig>(
      `/assets/projects/${code}/display/dashboards/${encodeURIComponent(dashboardId)}`,
      { method: "DELETE" },
    ),

  // -- data export to Rockfield Mercuto -------------------------------------
  mercutoSettings: (project: string) =>
    request<MercutoSettings>("/v2/integrations/mercuto", { params: { project } }),

  putMercutoSettings: (project: string, body: Record<string, unknown>) =>
    request<MercutoSettings>("/v2/integrations/mercuto", {
      method: "PUT",
      params: { project },
      body: JSON.stringify(body),
    }),

  /** Issues a new password for the consumer. The previous one stops working. */
  issueMercutoSubscriber: (project: string) =>
    request<MercutoSubscriber>("/v2/integrations/mercuto/subscriber", {
      method: "POST",
      params: { project },
    }),

  twinModels: (code: string) => request<TwinModelRow[]>(`/assets/projects/${code}/twin-models`),

  uploadTwinModel: (code: string, file: File, name?: string) => {
    const form = new FormData();
    form.append("file", file);
    if (name) form.append("name", name);
    return request<TwinModelRow>(`/assets/projects/${code}/twin-models`, {
      method: "POST",
      body: form,
    });
  },

  deleteTwinModel: (code: string, model: string) =>
    request<void>(`/assets/projects/${code}/twin-models/${encodeURIComponent(model)}`, {
      method: "DELETE",
    }),

  twinModelBytes: (code: string, model: string) =>
    fetchBytes(`/assets/projects/${code}/twin-models/${encodeURIComponent(model)}/file`),

  putDisplay: (
    code: string,
    section: Partial<Pick<DisplayConfig, "features" | "map" | "preferences" | "dashboards">>,
  ) =>
    request<DisplayConfig>(`/assets/projects/${code}/display`, {
      method: "PUT",
      body: JSON.stringify(section),
    }),
  devices: (project: string) =>
    request<Device[]>("/assets/devices", { params: { project, limit: 500 } }),

  // -- data ---------------------------------------------------------------
  channels: (project: string, params?: Record<string, unknown>) =>
    request<Channel[]>("/v2/data/channels", { params: { project, limit: 1000, ...params } }),

  /**
   * Largest per-event value in each bucket, with the event that produced it.
   *
   * Server-side because a year of crossings is hundreds of thousands of metric
   * samples; taking a daily maximum in the browser means downloading all of
   * them first.
   */
  metricExtremes: (params: {
    project: string;
    channels?: string[];
    start_time: string;
    end_time: string;
    bucket?: "hour" | "day" | "week" | "month";
    aggregate?: "abs_max" | "max" | "min";
    limit?: number;
  }) => request<MetricExtreme[]>("/v2/data/statistics/metric-extremes", { params }),

  latestSamples: (project: string) =>
    request<LatestSample[]>("/v2/data/statistics/latest-samples", { params: { project } }),

  secondarySamples: (channels: string[], start: string, end: string, limit = 1000) =>
    request<SecondarySample[]>("/v2/data/samples/secondary", {
      params: { channels, start_time: start, end_time: end, limit },
    }),

  metricSamples: (params: {
    project?: string;
    channels?: string[];
    start_time?: string;
    end_time?: string;
    limit?: number;
  }) => request<MetricSample[]>("/v2/data/samples/metric", { params }),

  /**
   * Series at the dashboard's interval and method, for telemetry and
   * per-event channels alike. `interval` is always explicit: the client
   * resolves "auto" so the badge and the data cannot disagree.
   */
  aggregatedSamples: (params: {
    channels: string[];
    start_time: string;
    end_time: string;
    interval: string;
    method: string;
    rolling?: boolean;
    max_points?: number;
  }) => request<AggregatedSamples>("/v2/data/samples/aggregated", { params }),

  /**
   * Post-process a device's mV/V channels into microstrain, from its `strain`
   * metadata - or remove those derived channels. They store nothing; history
   * is converted on read, so changing the gauge factor re-derives all of it.
   */
  /** Derived kN channels from the cell's rated capacity and output. */
  deriveLoad: (body: { device: string; enabled: boolean; dry_run?: boolean }) =>
    request<DeriveStrainResult>("/v2/data/channels/derive-load", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  deriveStrain: (body: { device: string; enabled: boolean; dry_run?: boolean }) =>
    request<DeriveStrainResult>("/v2/data/channels/derive-strain", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // -- train crossings ----------------------------------------------------
  /** The chosen extreme per interval, per channel, in the project's days. */
  crossingPeaks: (params: {
    project: string;
    quantity: string;
    start_time: string;
    end_time: string;
    interval: string;
    method?: string;
    devices?: string[];
    traffic_only?: boolean;
    /** Which events: crossings, idle samples, or both. Overrides traffic_only. */
    select?: "traffic" | "idle" | "all";
  }) => request<CrossingPeaks>("/v2/data/crossings/peaks", { params }),

  /** Crossings in a window with every per-event value, derived channels included. */
  crossingEvents: (params: {
    project: string;
    start_time: string;
    end_time: string;
    devices?: string[];
    limit?: number;
    traffic_only?: boolean;
  }) => request<CrossingEvents>("/v2/data/crossings/events", { params }),

  /** One crossing's per-second shape on the chosen devices, with a margin either side. */
  crossingTrace: (params: {
    project: string;
    event: string;
    devices?: string[];
    quantities?: string[];
    pad_s?: number;
  }) => request<CrossingTrace>("/v2/data/crossings/trace", { params }),

  crossingLatest: (project: string, quantity = "force") =>
    request<CrossingLatest>("/v2/data/crossings/latest", { params: { project, quantity } }),

  crossingSummary: (params: {
    project: string;
    start_time: string;
    end_time: string;
    devices?: string[];
    traffic_only?: boolean;
  }) => request<CrossingSummary>("/v2/data/crossings/summary", { params }),

  // -- events -------------------------------------------------------------
  events: (params: {
    project: string;
    limit?: number;
    offset?: number;
    start_time?: string;
    end_time?: string;
    ascending?: boolean;
    status?: string;
  }) => request<KEvent[]>("/v2/events/details", { params }),

  event: (code: string) => request<KEvent>(`/v2/events/details/${code}`),

  eventStatistics: (project: string, start?: string, end?: string) =>
    request<EventStatistics>("/v2/events/statistics", {
      params: { project, start_time: start, end_time: end },
    }),

  /**
   * Server-side downsampled waveform.
   *
   * `max_points` is the payload budget, not a zoom level: narrowing
   * start/end while holding it constant is how the chart zooms without ever
   * transferring more than a screen's worth of points.
   */
  waveform: (
    code: string,
    params?: {
      channels?: string[];
      max_points?: number;
      /** "none" returns every sample in the window, up to the endpoint's own ceiling. */
      mode?: "lttb" | "minmax" | "none";
      start_time?: string;
      end_time?: string;
    },
  ) => request<Waveform>(`/v2/events/${code}/waveform`, { params }),

  nearestEvent: (project: string, timestamp: string) =>
    request<KEvent | null>("/v2/events/search/nearest", { params: { project, timestamp } }),

  reprocessEvent: (code: string) =>
    request<KEvent>(`/v2/events/details/${code}/reprocess`, { method: "POST" }),

  // -- alerts -------------------------------------------------------------
  alarms: (project: string) => request<Alarm[]>("/v2/alerts/alarms", { params: { project } }),

  alarmLogs: (params: {
    project: string;
    active?: boolean;
    acknowledged?: string;
    cleared?: "any" | "true" | "false";
    start_time?: string;
    end_time?: string;
    limit?: number;
    offset?: number;
  }) => request<AlarmLog[]>("/v2/alerts/alarm-logs", { params }),

  clearAlarm: (code: string, reason: string) =>
    request<AlarmLog>(`/v2/alerts/alarm-logs/${code}/clear`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  alertLimits: (project: string, device?: string) =>
    request<ChannelLimit[]>("/v2/alerts/limits", { params: { project, device } }),

  setAlertLimits: (project: string, limits: ChannelLimitIn[]) =>
    request<ChannelLimit[]>("/v2/alerts/limits", {
      method: "PUT",
      body: JSON.stringify({ project, limits }),
    }),

  notifier: (project: string) => request<Notifier>("/v2/alerts/notifier", { params: { project } }),

  setNotifier: (project: string, body: Record<string, unknown>) =>
    request<Notifier>("/v2/alerts/notifier", {
      method: "PUT",
      params: { project },
      body: JSON.stringify(body),
    }),

  testNotifier: (project: string, recipient?: string) =>
    request<NotifierTest>("/v2/alerts/notifier/test", {
      method: "POST",
      params: { project },
      body: JSON.stringify({ recipient: recipient ?? null }),
    }),

  alarmSummary: (project: string) =>
    request<AlarmSummary>("/v2/alerts/alarm-statistics/summary", { params: { project } }),

  acknowledgeAlarm: (code: string, comment?: string) =>
    request<AlarmLog>(`/v2/alerts/alarm-logs/${code}/acknowledge`, {
      method: "POST",
      body: JSON.stringify({ comment: comment ?? null }),
    }),

  alarmComments: (code: string) =>
    request<AlarmComment[]>(`/v2/alerts/alarm-logs/${code}/comments`),

  addAlarmComment: (code: string, body: string) =>
    request<AlarmComment>(`/v2/alerts/alarm-logs/${code}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),

  // -- administration -----------------------------------------------------
  // Provisioning: everything needed to stand up a project, its instrumentation
  // and the people who can see it, without touching the database.

  createProject: (body: {
    name: string;
    description?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    timezone?: string | null;
    display_timezone?: string | null;
    project_number?: string | null;
    tenant?: string | null;
  }) => request<Project>("/assets/projects", { method: "POST", body: JSON.stringify(body) }),

  updateProject: (code: string, body: Record<string, unknown>) =>
    request<Project>(`/assets/projects/${code}`, { method: "PUT", body: JSON.stringify(body) }),

  /** The server refuses an active project; deactivate it first. */
  deleteProject: (code: string) =>
    request<void>(`/assets/projects/${code}`, { method: "DELETE" }),

  deviceTypes: () => request<DeviceType[]>("/assets/device-types"),

  createDeviceType: (body: {
    label: string;
    manufacturer?: string | null;
    model_number?: string | null;
    description?: string | null;
    icon?: string | null;
    template: DeviceTypeTemplate;
  }) =>
    request<DeviceType>("/assets/device-types", { method: "POST", body: JSON.stringify(body) }),

  createDevice: (body: {
    project: string;
    label: string;
    device_type: string;
    latitude?: number | null;
    longitude?: number | null;
    metadata?: Record<string, unknown>;
    channels?: NewChannel[];
  }) => request<Device>("/assets/devices", { method: "POST", body: JSON.stringify(body) }),

  updateDevice: (code: string, body: Record<string, unknown>) =>
    request<Device>(`/assets/devices/${code}`, { method: "PUT", body: JSON.stringify(body) }),

  deleteDevice: (code: string) =>
    request<void>(`/assets/devices/${code}`, { method: "DELETE" }),

  createChannel: (body: NewChannel & { project: string }) =>
    request<Channel>("/v2/data/channels", { method: "PUT", body: JSON.stringify(body) }),

  // -- users and access ----------------------------------------------------
  users: () => request<User[]>("/identity/users", { params: { limit: 200 } }),

  createUser: (body: {
    email: string;
    full_name?: string | null;
    password?: string | null;
    is_tenant_admin?: boolean;
    permission_groups?: string[];
    tenant?: string | null;
  }) => request<User>("/identity/users", { method: "PUT", body: JSON.stringify(body) }),

  updateUser: (code: string, body: Record<string, unknown>) =>
    request<User>(`/identity/users/${code}`, { method: "PATCH", body: JSON.stringify(body) }),

  deleteUser: (code: string) => request<void>(`/identity/users/${code}`, { method: "DELETE" }),

  grantUser: (code: string, permission_groups: string[]) =>
    request<User>(`/identity/users/${code}/grant`, {
      method: "POST",
      body: JSON.stringify({ permission_groups }),
    }),

  permissionGroups: () => request<PermissionGroup[]>("/identity/permissions"),

  createPermissionGroup: (body: {
    name: string;
    description?: string | null;
    grants: Grant[];
  }) =>
    request<PermissionGroup>("/identity/permissions", {
      method: "PUT",
      body: JSON.stringify(body),
    }),

  apiKeys: (userCode: string) =>
    request<ApiKey[]>(`/identity/users/${userCode}/api_keys`),

  createApiKey: (userCode: string, description: string) =>
    request<ApiKey & { key: string }>(`/identity/users/${userCode}/api_keys`, {
      method: "POST",
      body: JSON.stringify({ description }),
    }),

  deleteApiKey: (userCode: string, keyCode: string) =>
    request<void>(`/identity/users/${userCode}/api_keys/${keyCode}`, { method: "DELETE" }),

  deviceKeys: (project: string) =>
    request<DeviceKey[]>("/identity/device-keys", { params: { project } }),

  createDeviceKey: (body: {
    project: string;
    description: string;
    access_policy?: "ingest" | "readonly" | "readwrite";
  }) =>
    request<DeviceKey & { key: string }>("/identity/device-keys", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  deleteDeviceKey: (code: string) =>
    request<void>(`/identity/device-keys/${code}`, { method: "DELETE" }),

  deviceBootstrap: (project: string, device: string) =>
    request<DeviceBootstrap>(`/v2/data/devices/${device}/bootstrap`, { params: { project } }),

  // -- fatigue ------------------------------------------------------------
  remnantCapacity: (project: string, start: string, end: string) =>
    request<RemnantCapacity[]>("/fatigue/connection-data/remnant-capacity", {
      params: { project, start_time: start, end_time: end },
    }),

  cycleCounts: (project: string, start: string, end: string) =>
    request<{
      project: string;
      bin_size: number;
      channels: string[];
      total_cycles: number;
      bins: { bin_index: number; range_low: number; range_high: number; counts: Record<string, number> }[];
    }>("/fatigue/rainflow/cycle_counts", {
      params: { project, start_time: start, end_time: end, format: "json" },
    }),

  /** Spectrum, daily load bands and node lifetime fatigue for one window. */
  fatigueSummary: (project: string, start: string, end: string, devices?: string[]) =>
    request<FatigueSummary>("/fatigue/summary", {
      params: { project, start_time: start, end_time: end, devices },
    }),

  eventCycleTimeline: (project: string, start: string, end: string, channel?: string) =>
    request<EventCycleTimeline>("/fatigue/rainflow/events", {
      params: { project, start_time: start, end_time: end, channel },
    }),

  eventCycleHistogram: (event: string) =>
    request<EventCycleHistogram>(`/fatigue/rainflow/events/${event}`),
};
